/**
 * RFQ Create Wizard Step Components
 *
 * Extracted from RFQCreateWizard.tsx - renders individual wizard steps.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Plus,
  Trash2,
  FileText,
  Building,
} from 'lucide-react';

import type {
  RFQCategory,
  RFQPriority,
  RFQContractType,
  RFQContractFrequency,
} from '../../lib/api';
import type {
  BasicDetailsForm,
  LineItemForm,
  AdditionalCostForm,
  RequiredCertificateForm,
  SupplierOption,
} from './rfqWizardTypes';

/* ------------------------------------------------------------------ */
/*  Step 1: Basic Details                                              */
/* ------------------------------------------------------------------ */

interface Step1Props {
  basicDetails: BasicDetailsForm;
  setBasicDetails: (details: BasicDetailsForm) => void;
}

export function BasicDetailsStep({ basicDetails, setBasicDetails }: Step1Props) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label htmlFor="title">RFQ Title *</Label>
          <Input
            id="title"
            value={basicDetails.title}
            onChange={(e) => setBasicDetails({ ...basicDetails, title: e.target.value })}
            placeholder="e.g., Supply of Facing Bricks - Oakwood Gardens"
          />
        </div>

        <div className="col-span-2">
          <Label htmlFor="description">Description *</Label>
          <Textarea
            id="description"
            value={basicDetails.description}
            onChange={(e) => setBasicDetails({ ...basicDetails, description: e.target.value })}
            placeholder="Detailed requirements and specifications..."
            rows={4}
          />
        </div>

        <div>
          <Label>Category</Label>
          <select
            value={basicDetails.category}
            onChange={(e) => setBasicDetails({ ...basicDetails, category: e.target.value as RFQCategory })}
            className="w-full p-2 border rounded-md bg-background text-foreground"
          >
            <option value="materials">Materials</option>
            <option value="services">Services</option>
            <option value="equipment">Equipment</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <Label>Priority</Label>
          <select
            value={basicDetails.priority}
            onChange={(e) => setBasicDetails({ ...basicDetails, priority: e.target.value as RFQPriority })}
            className="w-full p-2 border rounded-md bg-background text-foreground"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        <div>
          <Label>Currency</Label>
          <select
            value={basicDetails.currency}
            onChange={(e) => setBasicDetails({ ...basicDetails, currency: e.target.value })}
            className="w-full p-2 border rounded-md bg-background text-foreground"
          >
            <option value="GBP">GBP (&pound;)</option>
            <option value="USD">USD ($)</option>
            <option value="EUR">EUR (&euro;)</option>
          </select>
        </div>

        <div>
          <Label htmlFor="deadline">Response Deadline *</Label>
          <Input
            id="deadline"
            type="date"
            value={basicDetails.response_deadline}
            onChange={(e) => setBasicDetails({ ...basicDetails, response_deadline: e.target.value })}
            min={new Date().toISOString().split('T')[0]}
          />
        </div>

        <div>
          <Label>Contract Type</Label>
          <select
            value={basicDetails.contract_type}
            onChange={(e) => setBasicDetails({ ...basicDetails, contract_type: e.target.value as RFQContractType })}
            className="w-full p-2 border rounded-md bg-background text-foreground"
          >
            <option value="one_time">One-Time Purchase</option>
            <option value="regular_running">Regular/Running Contract</option>
          </select>
        </div>

        {basicDetails.contract_type === 'regular_running' && (
          <div>
            <Label>Contract Frequency</Label>
            <select
              value={basicDetails.contract_frequency || ''}
              onChange={(e) => setBasicDetails({ ...basicDetails, contract_frequency: e.target.value as RFQContractFrequency })}
              className="w-full p-2 border rounded-md bg-background text-foreground"
            >
              <option value="">Select frequency</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
        )}

        <div>
          <Label>Validity Period (days)</Label>
          <Input
            type="number"
            value={basicDetails.validity_period_days}
            onChange={(e) => setBasicDetails({ ...basicDetails, validity_period_days: parseInt(e.target.value) || 30 })}
            min={1}
          />
        </div>

        <div className="col-span-2">
          <Label>Terms & Conditions</Label>
          <Textarea
            value={basicDetails.terms_and_conditions}
            onChange={(e) => setBasicDetails({ ...basicDetails, terms_and_conditions: e.target.value })}
            placeholder="Payment terms, delivery terms, etc."
            rows={3}
          />
        </div>

        <div className="col-span-2">
          <Label>Special Requirements</Label>
          <Textarea
            value={basicDetails.special_requirements}
            onChange={(e) => setBasicDetails({ ...basicDetails, special_requirements: e.target.value })}
            placeholder="Any special requirements or conditions..."
            rows={2}
          />
        </div>

        <div className="col-span-2 flex gap-6">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="nda"
              checked={basicDetails.nda_required}
              onCheckedChange={(checked) => setBasicDetails({ ...basicDetails, nda_required: !!checked })}
            />
            <Label htmlFor="nda">NDA Required</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="autoContract"
              checked={basicDetails.auto_create_contract}
              onCheckedChange={(checked) => setBasicDetails({ ...basicDetails, auto_create_contract: !!checked })}
            />
            <Label htmlFor="autoContract">Auto-create contract on award</Label>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step 2: Line Items                                                 */
/* ------------------------------------------------------------------ */

interface Step2Props {
  lineItems: LineItemForm[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof LineItemForm, value: string | number | null) => void;
}

export function LineItemsStep({ lineItems, onAdd, onRemove, onUpdate }: Step2Props) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Line Items</h3>
        <Button type="button" variant="outline" size="sm" onClick={onAdd}>
          <Plus className="h-4 w-4 mr-1" /> Add Item
        </Button>
      </div>

      {lineItems.map((item, index) => (
        <Card key={index}>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-sm">Line {index + 1}</CardTitle>
              {lineItems.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => onRemove(index)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Description *</Label>
              <Input
                value={item.item_description}
                onChange={(e) => onUpdate(index, 'item_description', e.target.value)}
                placeholder="Item description"
              />
            </div>
            <div>
              <Label className="text-xs">Part Number *</Label>
              <Input
                value={item.part_number}
                onChange={(e) => onUpdate(index, 'part_number', e.target.value)}
                placeholder="PN-001"
              />
            </div>
            <div>
              <Label className="text-xs">Quantity *</Label>
              <Input
                type="number"
                value={item.quantity}
                onChange={(e) => onUpdate(index, 'quantity', parseInt(e.target.value) || 0)}
                min={1}
              />
            </div>
            <div>
              <Label className="text-xs">Unit</Label>
              <Input
                value={item.unit_of_measure}
                onChange={(e) => onUpdate(index, 'unit_of_measure', e.target.value)}
                placeholder="Each"
              />
            </div>
            <div>
              <Label className="text-xs">Target Unit Price</Label>
              <Input
                type="number"
                value={item.target_unit_price || ''}
                onChange={(e) => onUpdate(index, 'target_unit_price', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="0.00"
                step="0.01"
              />
            </div>
            {/* CR-5467: Delivery Location + Required Date moved off the
                line item — they belong at RFQ header level. The header
                form add lands in the follow-up; for now the wizard
                exposes a single header-level Required Date via the
                response_deadline (already present at Step 1). */}
            <div>
              <Label className="text-xs">Drawing Issue / Revision</Label>
              <Input
                value={item.drawing_issue}
                onChange={(e) => onUpdate(index, 'drawing_issue', e.target.value)}
                placeholder="e.g. Rev B, Issue 3"
              />
            </div>
            <div className="col-span-3">
              <Label className="text-xs">Technical Specifications</Label>
              <Textarea
                value={item.technical_specifications}
                onChange={(e) => onUpdate(index, 'technical_specifications', e.target.value)}
                placeholder="Technical specs..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step 3: Additional Costs                                           */
/* ------------------------------------------------------------------ */

interface Step3Props {
  costs: AdditionalCostForm[];
  currency: string;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof AdditionalCostForm, value: string | number) => void;
}

export function AdditionalCostsStep({ costs, currency, onAdd, onRemove, onUpdate }: Step3Props) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold">Additional Costs</h3>
          <p className="text-sm text-muted-foreground">Tooling, packaging, transport costs (optional)</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onAdd}>
          <Plus className="h-4 w-4 mr-1" /> Add Cost
        </Button>
      </div>

      {costs.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8 text-muted-foreground">
            No additional costs added. Click &quot;Add Cost&quot; to include tooling, packaging, or transport costs.
          </CardContent>
        </Card>
      ) : (
        costs.map((cost, index) => (
          <Card key={index}>
            <CardContent className="pt-4 grid grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Cost Type</Label>
                <select
                  value={cost.cost_type}
                  onChange={(e) => onUpdate(index, 'cost_type', e.target.value)}
                  className="w-full p-2 border rounded-md text-sm"
                >
                  <option value="tooling">Tooling</option>
                  <option value="packaging">Packaging</option>
                  <option value="transport">Transport</option>
                  <option value="setup">Setup</option>
                  <option value="certification">Certification</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Description</Label>
                <Input
                  value={cost.description}
                  onChange={(e) => onUpdate(index, 'description', e.target.value)}
                  placeholder="Cost description"
                />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Amount ({currency})</Label>
                  <Input
                    type="number"
                    value={cost.amount}
                    onChange={(e) => onUpdate(index, 'amount', parseFloat(e.target.value) || 0)}
                    step="0.01"
                  />
                </div>
                <Button variant="ghost" size="sm" className="mt-5" onClick={() => onRemove(index)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step 4: Documents & Certificates                                   */
/* ------------------------------------------------------------------ */

interface Step4Props {
  certificates: RequiredCertificateForm[];
  ndaRequired: boolean;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof RequiredCertificateForm, value: string | number | boolean) => void;
}

export function DocumentsStep({ certificates, ndaRequired, onAdd, onRemove, onUpdate }: Step4Props) {
  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="font-semibold">Required Certificates</h3>
            <p className="text-sm text-muted-foreground">Certificates suppliers must provide</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={onAdd}>
            <Plus className="h-4 w-4 mr-1" /> Add Certificate
          </Button>
        </div>

        {certificates.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8 text-muted-foreground">
              No certificates required. Click &quot;Add Certificate&quot; to require ISO, material certs, etc.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {certificates.map((cert, index) => (
              <Card key={index}>
                <CardContent className="pt-4 grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Type</Label>
                    <select
                      value={cert.certificate_type}
                      onChange={(e) => onUpdate(index, 'certificate_type', e.target.value)}
                      className="w-full p-2 border rounded-md text-sm"
                    >
                      <option value="iso_9001">ISO 9001</option>
                      <option value="iso_14001">ISO 14001</option>
                      <option value="iso_45001">ISO 45001</option>
                      <option value="reach">REACH</option>
                      <option value="rohs">RoHS</option>
                      <option value="ce_marking">CE Marking</option>
                      <option value="material_cert">Material Certificate</option>
                      <option value="test_cert">Test Certificate</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={cert.certificate_name}
                      onChange={(e) => onUpdate(index, 'certificate_name', e.target.value)}
                      placeholder="Certificate name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Validity (days)</Label>
                    <Input
                      type="number"
                      value={cert.expiry_tolerance_days}
                      onChange={(e) => onUpdate(index, 'expiry_tolerance_days', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={cert.is_mandatory}
                        onCheckedChange={(checked) => onUpdate(index, 'is_mandatory', !!checked)}
                      />
                      <Label className="text-xs">Mandatory</Label>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => onRemove(index)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {ndaRequired && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="h-4 w-4" />
              NDA Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Suppliers will be required to accept an NDA before viewing RFQ details.
              You can upload the NDA document after creating the RFQ.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step 5: Supplier Selection                                         */
/* ------------------------------------------------------------------ */

interface Step5Props {
  suppliers: SupplierOption[];
  selectedSuppliers: number[];
  onToggle: (companyId: number) => void;
}

export function SupplierSelectionStep({ suppliers, selectedSuppliers, onToggle }: Step5Props) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold">Select Suppliers</h3>
        <p className="text-sm text-muted-foreground">
          Choose which suppliers should receive this RFQ. Leave empty to send later.
        </p>
      </div>

      <div className="border rounded-lg max-h-96 overflow-y-auto">
        {suppliers.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No suppliers available
          </div>
        ) : (
          suppliers.map((supplier) => (
            <div
              key={supplier.id}
              className={`flex items-center p-3 border-b last:border-b-0 cursor-pointer hover:bg-muted/50 ${
                selectedSuppliers.includes(supplier.company) ? 'bg-primary/5' : ''
              }`}
              onClick={() => onToggle(supplier.company)}
            >
              <Checkbox
                checked={selectedSuppliers.includes(supplier.company)}
                onCheckedChange={() => onToggle(supplier.company)}
                onClick={(e) => e.stopPropagation()}
              />
              <Building className="h-4 w-4 ml-3 mr-2 text-muted-foreground" />
              <span className="font-medium">{supplier.company_name}</span>
            </div>
          ))
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        {selectedSuppliers.length} supplier(s) selected
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step 6: Review                                                     */
/* ------------------------------------------------------------------ */

interface Step6Props {
  basicDetails: BasicDetailsForm;
  lineItems: LineItemForm[];
  additionalCosts: AdditionalCostForm[];
  requiredCertificates: RequiredCertificateForm[];
  selectedSuppliers: number[];
  suppliers: SupplierOption[];
  totalValue: number;
}

export function ReviewStep({
  basicDetails,
  lineItems,
  additionalCosts,
  requiredCertificates,
  selectedSuppliers,
  suppliers,
  totalValue,
}: Step6Props) {
  return (
    <div className="space-y-6">
      <h3 className="font-semibold">Review RFQ</h3>

      <Card>
        <CardHeader>
          <CardTitle>{basicDetails.title || 'Untitled RFQ'}</CardTitle>
          <CardDescription>{basicDetails.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Category:</span>
              <span className="ml-2 font-medium capitalize">{basicDetails.category}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Priority:</span>
              <Badge className="ml-2" variant="outline">{basicDetails.priority.toUpperCase()}</Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Currency:</span>
              <span className="ml-2 font-medium">{basicDetails.currency}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Deadline:</span>
              <span className="ml-2 font-medium">{basicDetails.response_deadline}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Contract:</span>
              <span className="ml-2 font-medium capitalize">{basicDetails.contract_type.replace('_', ' ')}</span>
            </div>
            <div>
              <span className="text-muted-foreground">NDA:</span>
              <span className="ml-2 font-medium">{basicDetails.nda_required ? 'Required' : 'Not Required'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Line Items ({lineItems.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {lineItems.map((item, index) => (
              <div key={index} className="flex justify-between text-sm py-1 border-b last:border-b-0">
                <span>{item.item_description || `Item ${index + 1}`}</span>
                <span className="font-medium">
                  {item.quantity} x {basicDetails.currency} {item.target_unit_price?.toFixed(2) || '0.00'}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {additionalCosts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Additional Costs ({additionalCosts.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {additionalCosts.map((cost, index) => (
                <div key={index} className="flex justify-between text-sm py-1 border-b last:border-b-0">
                  <span className="capitalize">{cost.cost_type}: {cost.description}</span>
                  <span className="font-medium">{basicDetails.currency} {cost.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {requiredCertificates.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Required Certificates ({requiredCertificates.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {requiredCertificates.map((cert, index) => (
                <Badge key={index} variant={cert.is_mandatory ? 'default' : 'secondary'}>
                  {cert.certificate_name || cert.certificate_type.replace('_', ' ').toUpperCase()}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Selected Suppliers ({selectedSuppliers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {selectedSuppliers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No suppliers selected. RFQ will be saved as draft.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selectedSuppliers.map(companyId => {
                const supplier = suppliers.find(s => s.company === companyId);
                return (
                  <Badge key={companyId} variant="outline">
                    {supplier?.company_name || `Company ${companyId}`}
                  </Badge>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-muted/50">
        <CardContent className="pt-4">
          <div className="flex justify-between items-center text-lg font-semibold">
            <span>Estimated Total Value:</span>
            <span>{basicDetails.currency} {totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
